/**
 * SKI AXIS · /admin/forum/dashboard
 *
 * Forum command-center for Dr. Neha Dewan, Ross + Nitish.
 * Single page, auto-refreshing, pulls from /api/admin/forum/stats.
 *
 * Sections:
 *   1. Hero · countdown + revenue vs target
 *   2. Funnel cards · paid / comp / sponsors / revenue
 *   3. Daily curve · last 21 days
 *   4. Latest paid registrations
 *   5. Sponsor pipeline
 *   6. Abandoned checkouts (recoverable revenue)
 *   7. Lead-pipeline gross adds by source
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 60_000; // auto-refresh every 60s

const fmtAED = (n) => `AED ${Number(n || 0).toLocaleString('en-AE', { maximumFractionDigits: 0 })}`;
const fmtUSD = (n) => `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtPct = (pct) => `${Math.max(0, Math.min(100, Math.round(pct)))}%`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

export default function AdminForumDashboardPage() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem('admin_api_key') || '');
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [cc, setCc] = useState(null);  // command-center 2.0 compound payload
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);

  useEffect(() => {
    document.title = 'Forum · Command Center · SKI AXIS';
    if (adminKey) load(adminKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!authed) return;
    const t = setInterval(() => load(adminKey), REFRESH_MS);
    return () => clearInterval(t);
  }, [authed, adminKey]);

  const load = async (key) => {
    setErr(''); setLoading(true);
    try {
      // Fire both endpoints in parallel for speed
      const [statsResp, ccResp] = await Promise.all([
        axios.get(`${API}/admin/forum/stats`, { params: { admin_key: key } }),
        axios.get(`${API}/admin/forum/command-center`, { params: { admin_key: key } }).catch(() => ({ data: null })),
      ]);
      setStats(statsResp.data);
      setCc(ccResp.data);
      setAuthed(true);
      localStorage.setItem('admin_api_key', key);
      setRefreshedAt(new Date());
    } catch (e) {
      setErr(e?.response?.status === 403 ? 'Invalid admin key.' : 'Could not load Forum stats.');
      setAuthed(false);
    } finally { setLoading(false); }
  };

  const paidPct = useMemo(() => {
    if (!stats) return 0;
    return (stats.actual.paid / Math.max(1, stats.targets.paid)) * 100;
  }, [stats]);
  const compPct = useMemo(() => {
    if (!stats) return 0;
    return (stats.actual.comp / Math.max(1, stats.targets.comp)) * 100;
  }, [stats]);
  const revPct = useMemo(() => {
    if (!stats) return 0;
    return (stats.actual.revenue_usd / Math.max(1, stats.targets.revenue_usd)) * 100;
  }, [stats]);

  // ---------- Auth gate ----------
  if (!authed) {
    return (
      <div data-testid="forum-dashboard-auth" style={{ minHeight: '100vh', background: '#FAF7F0', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'Georgia, serif' }}>
        <div style={{ width: '100%', maxWidth: 460, background: '#FFFFFF', padding: 36, border: '1px solid #E8DFC9' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.32em', color: '#C8A84B', textTransform: 'uppercase', marginBottom: 10 }}>SKI Axis · Forum command center</div>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 28, lineHeight: 1.2, fontWeight: 700, margin: '0 0 18px', color: '#0D1B2A' }}>Admin access</h1>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: '#3D4A5C', marginTop: 0 }}>Paste your admin key to unlock the live Forum dashboard.</p>
          <input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load(adminKey)}
            placeholder="Admin key"
            data-testid="admin-key-input"
            style={{ width: '100%', padding: '14px 16px', fontSize: 14, border: '1px solid #E8DFC9', background: '#FAF7F0', marginBottom: 12, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
          />
          <button
            onClick={() => load(adminKey)}
            disabled={loading || !adminKey}
            data-testid="admin-key-submit"
            style={{ width: '100%', padding: '14px', background: '#0D1B2A', color: '#C8A84B', border: '1px solid #0D1B2A', fontSize: 12, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            {loading ? 'Checking…' : 'Unlock dashboard'}
          </button>
          {err && <div data-testid="auth-error" style={{ marginTop: 12, padding: '10px 14px', background: '#FBEAEA', color: '#B0341E', fontSize: 13, border: '1px solid #E8C0B8' }}>{err}</div>}
        </div>
      </div>
    );
  }

  if (!stats) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAF7F0', fontFamily: 'Georgia, serif', color: '#3D4A5C', fontStyle: 'italic' }}>Loading Forum stats…</div>;
  }

  return (
    <div data-testid="forum-dashboard" style={{ minHeight: '100vh', background: '#FAF7F0', fontFamily: 'Georgia, serif', color: '#0D1B2A', paddingBottom: 60 }}>

      {/* HEADER */}
      <div style={{ background: 'linear-gradient(135deg, #0D1B2A 0%, #1A2F4A 100%)', color: '#FAF7F0', padding: '32px 40px', borderBottom: '3px solid #C8A84B' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 18 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.32em', color: '#C8A84B', textTransform: 'uppercase', marginBottom: 8 }}>SKI Axis · Forum command center</div>
            <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 32, fontWeight: 700, lineHeight: 1.15, margin: 0, letterSpacing: '-.012em' }}>
              Invest UAE Forum 2026 · <em style={{ color: '#C8A84B', fontStyle: 'italic' }}>31 May</em>
            </h1>
            <div style={{ fontSize: 14, color: 'rgba(250,247,240,.78)', marginTop: 8, fontStyle: 'italic' }}>
              {stats.forum.passed
                ? <>Forum complete — review the receipts.</>
                : <>{stats.forum.days_to_forum} days to go · Sunday, 6 PM BST · live online</>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div data-testid="last-refreshed" style={{ fontSize: 11, letterSpacing: '.16em', color: 'rgba(250,247,240,.6)', textTransform: 'uppercase', marginBottom: 6 }}>
              Live · auto-refresh 60s
            </div>
            <div style={{ fontSize: 12, color: 'rgba(250,247,240,.5)' }}>
              {refreshedAt ? `Refreshed ${refreshedAt.toLocaleTimeString('en-GB')}` : '—'}
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button onClick={() => load(adminKey)} disabled={loading} data-testid="refresh-btn" style={{ padding: '8px 16px', background: 'transparent', color: '#C8A84B', border: '1px solid #C8A84B', fontSize: 10.5, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit' }}>
                {loading ? 'Loading…' : 'Refresh now'}
              </button>
              <Link to="/admin/leads" style={{ padding: '8px 16px', background: 'transparent', color: 'rgba(250,247,240,.78)', border: '1px solid rgba(250,247,240,.3)', fontSize: 10.5, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase', textDecoration: 'none', fontFamily: 'inherit' }}>
                Leads →
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '32px 40px' }}>

        {/* COMMAND CENTER 2.0 — investor-screenshot panel (sales · recovery · cohort · visitors) */}
        {cc && <CommandCenterPanel cc={cc} />}

        {/* KPI CARDS */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 32 }}>
          <KpiCard label="Paid delegates" value={stats.actual.paid} target={stats.targets.paid} suffix={`/ ${stats.targets.paid}`} pct={paidPct} tone="#C8A84B" testId="kpi-paid" />
          <KpiCard label="Complimentary" value={stats.actual.comp} target={stats.targets.comp} suffix={`/ ${stats.targets.comp}`} pct={compPct} tone="#3A4A5C" testId="kpi-comp" />
          <KpiCard label="Total registered" value={stats.actual.total_registered} suffix="registrations" pct={null} tone="#0D1B2A" testId="kpi-total" />
          <KpiCard label="Revenue (live)" value={fmtUSD(stats.actual.revenue_usd)} target={fmtUSD(stats.targets.revenue_usd)} suffix={fmtAED(stats.actual.revenue_aed)} pct={revPct} tone="#8A6A14" testId="kpi-revenue" big />
        </div>

        {/* DAILY CURVE */}
        <Section title="Registration curve · last 21 days" testId="daily-curve-section">
          <DailyCurve data={stats.daily_curve} target={stats.targets.paid} days={stats.forum.days_to_forum} />
        </Section>

        {/* LATEST PAID + ABANDONED */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 24, marginBottom: 32 }}>
          <Section title={`Latest paid registrations · ${stats.latest_paid.length}`} testId="latest-paid-section" flat>
            {stats.latest_paid.length === 0 ? (
              <Empty text="No paid registrations yet. They will appear here within seconds of checkout." />
            ) : (
              <TableSlim
                cols={['Name', 'Email', 'When']}
                rows={stats.latest_paid.map(r => [r.full_name || '—', r.email || '—', fmtDate(r.created_at)])}
              />
            )}
          </Section>
          <Section title={`Abandoned checkouts · ${stats.abandoned_checkouts.length}`} testId="abandoned-section" flat>
            <p style={{ fontSize: 12.5, color: '#7A8190', fontStyle: 'italic', margin: '0 0 12px' }}>People who started checkout but didn't pay — recoverable with a personal nudge.</p>
            {stats.abandoned_checkouts.length === 0 ? (
              <Empty text="No abandoned checkouts in the last 21 days." />
            ) : (
              <TableSlim
                cols={['Email', 'Amount', 'When']}
                rows={stats.abandoned_checkouts.map(r => [r.email || '—', fmtAED(r.amount), fmtDate(r.created_at)])}
              />
            )}
          </Section>
        </div>

        {/* SPONSOR PIPELINE */}
        <Section title={`Sponsor pipeline · ${stats.sponsor_pipeline.count}`} testId="sponsor-section">
          {stats.sponsor_pipeline.count === 0 ? (
            <Empty text='No sponsor inquiries logged yet. As leads land via /api/axis-circuit/inquiry with intent="sponsor", they appear here.' />
          ) : (
            <>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
                {Object.entries(stats.sponsor_pipeline.by_status).map(([st, count]) => (
                  <Pill key={st} label={st} value={count} />
                ))}
              </div>
              <TableSlim
                cols={['Name', 'Org', 'Status', 'When']}
                rows={stats.sponsor_pipeline.latest.map(s => [
                  s.full_name || '—',
                  s.organisation || '—',
                  s.status || 'new',
                  fmtDate(s.created_at),
                ])}
              />
            </>
          )}
        </Section>

        {/* ROLE BREAKDOWN + LEAD SOURCES */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 24, marginBottom: 32 }}>
          <Section title="Registrations by role" testId="role-breakdown-section" flat>
            {Object.keys(stats.actual.by_role).length === 0 ? (
              <Empty text="No registrations yet." />
            ) : (
              Object.entries(stats.actual.by_role).map(([role, count]) => (
                <Bar key={role} label={role} value={count} max={stats.actual.total_registered || 1} tone="#C8A84B" />
              ))
            )}
          </Section>
          <Section title={`Lead-pipeline gross adds · last 21 days · ${stats.leads.last_21d_total}`} testId="leads-21d-section" flat>
            {stats.leads.last_21d_total === 0 ? (
              <Empty text="No leads in the last 21 days." />
            ) : (
              Object.entries(stats.leads.by_type).sort(([,a],[,b]) => b-a).map(([type, count]) => (
                <Bar key={type} label={type} value={count} max={stats.leads.last_21d_total || 1} tone="#3A4A5C" />
              ))
            )}
          </Section>
        </div>

        {/* FOOTER */}
        <div style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid #E8DFC9', fontSize: 12, color: '#7A8190', fontStyle: 'italic', textAlign: 'center' }}>
          Pulled live from MongoDB · Stripe LIVE mode verified · auto-refresh every 60s · {refreshedAt ? `last refresh ${refreshedAt.toLocaleString('en-GB')}` : '—'}
        </div>

      </div>
    </div>
  );
}

// ---------- Sub-components ----------

function KpiCard({ label, value, target, suffix, pct, tone, testId, big }) {
  return (
    <div data-testid={testId} style={{ background: '#FFFFFF', border: '1px solid #E8DFC9', padding: '20px 22px', borderLeft: `3px solid ${tone}` }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.22em', color: tone, textTransform: 'uppercase', marginBottom: 10 }}>{label}</div>
      <div style={{ fontFamily: 'Georgia, serif', fontSize: big ? 30 : 32, fontWeight: 700, color: '#0D1B2A', letterSpacing: '-.012em', lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#7A8190', marginTop: 6 }}>{suffix}</div>
      {pct !== null && pct !== undefined && (
        <div style={{ marginTop: 12, height: 4, background: '#FBF8EE', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: fmtPct(pct), background: tone, transition: 'width .4s' }} />
        </div>
      )}
    </div>
  );
}

function Section({ title, testId, children, flat }) {
  return (
    <section data-testid={testId} style={{ background: '#FFFFFF', border: '1px solid #E8DFC9', padding: '24px 28px', marginBottom: flat ? 0 : 28 }}>
      <h2 style={{ fontFamily: 'Georgia, serif', fontSize: 19, fontWeight: 700, color: '#0D1B2A', margin: '0 0 18px', letterSpacing: '-.005em' }}>{title}</h2>
      {children}
    </section>
  );
}

function Empty({ text }) {
  return <div style={{ padding: '24px 16px', textAlign: 'center', color: '#A89F8E', fontStyle: 'italic', fontSize: 13 }}>{text}</div>;
}

function Pill({ label, value }) {
  return (
    <div style={{ background: '#FBF8EE', border: '1px solid #E8DFC9', padding: '6px 12px', fontSize: 12, color: '#0D1B2A' }}>
      <span style={{ textTransform: 'uppercase', letterSpacing: '.14em', fontSize: 10.5, fontWeight: 700, color: '#8A6A14', marginRight: 8 }}>{label}</span>
      <span style={{ fontWeight: 700 }}>{value}</span>
    </div>
  );
}

function Bar({ label, value, max, tone }) {
  const pct = (value / max) * 100;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
        <span style={{ textTransform: 'capitalize', color: '#0D1B2A' }}>{String(label).replace(/_/g, ' ')}</span>
        <span style={{ fontWeight: 700, color: '#0D1B2A' }}>{value}</span>
      </div>
      <div style={{ height: 6, background: '#FBF8EE', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: fmtPct(pct), background: tone, transition: 'width .4s' }} />
      </div>
    </div>
  );
}

function TableSlim({ cols, rows }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#FBF8EE', color: '#8A6A14' }}>
            {cols.map((c, i) => (
              <th key={i} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 10.5, letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700, borderBottom: '1px solid #E8DFC9' }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #F0E9D6' }}>
              {row.map((cell, j) => (
                <td key={j} style={{ padding: '9px 12px', color: '#0D1B2A' }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DailyCurve({ data, target, days }) {
  if (!data || data.length === 0) {
    return <Empty text="No daily activity in the last 21 days yet — when registrations land they will plot here." />;
  }
  const max = Math.max(1, ...data.map(d => d.total));
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 140, padding: '0 4px 8px', borderBottom: '1px solid #E8DFC9' }}>
        {data.map((d, i) => (
          <div key={d.day || i} title={`${d.day} · ${d.total} total · ${d.paid} paid`} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minWidth: 12, cursor: 'help' }}>
            <div style={{ height: `${(d.total / max) * 100}%`, background: '#E8DFC9' }}>
              <div style={{ height: `${(d.paid / Math.max(1, d.total)) * 100}%`, background: '#C8A84B' }} />
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#7A8190', marginTop: 8 }}>
        <span>21 days ago</span>
        <span style={{ display: 'flex', gap: 14 }}>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#C8A84B', marginRight: 6, verticalAlign: 'middle' }} />Paid</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#E8DFC9', marginRight: 6, verticalAlign: 'middle' }} />All registrations</span>
        </span>
        <span>Today · {days}d to Forum</span>
      </div>
    </div>
  );
}

// --------------------------------------------------------------
// Command Center 2.0 — investor-screenshot-ready compound panel
// Lays out: ticket sales · recovery funnel · cohort apps · visitors
// --------------------------------------------------------------
function CommandCenterPanel({ cc }) {
  const t = cc?.tickets || {};
  const r = cc?.recovery_funnel || {};
  const c = cc?.cohort || {};
  const v = cc?.visitors;

  return (
    <div data-testid="cc2-panel" style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.32em', color: '#C8A84B', textTransform: 'uppercase', marginBottom: 4 }}>Command Center 2.0 · Investor Snapshot</div>
          <div style={{ fontSize: 13.5, color: '#3D4A5C', fontStyle: 'italic', fontFamily: 'Georgia, serif' }}>One-screen view for sponsor / investor conversations. Refreshes every 60s.</div>
        </div>
        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.18em', color: '#0D7A3E', textTransform: 'uppercase' }} data-testid="cc2-as-of">
          ● Live · {cc?.as_of ? new Date(cc.as_of).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—'}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>

        {/* CARD 1 · LIVE TICKET SALES */}
        <CcCard tone="#C8A84B" label="Live Ticket Sales" testId="cc2-tickets" gradient>
          <div style={{ fontFamily: 'Georgia, serif', fontSize: 38, fontWeight: 700, lineHeight: 1, color: '#0D1B2A', letterSpacing: '-.012em', marginBottom: 4 }}>
            {fmtAED(t.revenue_aed)}
          </div>
          <div style={{ fontSize: 12, color: '#3D4A5C', marginBottom: 14 }}>
            ≈ {fmtUSD(t.revenue_usd)} · <strong style={{ color: '#0D1B2A' }}>{t.paid_count || 0}</strong> paid {t.target_paid ? <>of {t.target_paid}</> : null}
          </div>
          <CcStat label="Today" value={t.paid_today ?? 0} suffix="paid" />
          <CcStat label="Last 7 days" value={t.paid_last_7d ?? 0} suffix="paid" />
          <CcStat label="Goal completion" value={fmtPct(t.completion_pct ?? 0)} bar pct={t.completion_pct ?? 0} />
        </CcCard>

        {/* CARD 2 · RECOVERY FUNNEL */}
        <CcCard tone="#8A6A14" label="Recovery Funnel" testId="cc2-recovery">
          <div style={{ fontFamily: 'Georgia, serif', fontSize: 38, fontWeight: 700, lineHeight: 1, color: '#0D1B2A', letterSpacing: '-.012em', marginBottom: 4 }}>
            {r.recovered ?? 0}<span style={{ fontSize: 18, color: '#7A8190', fontWeight: 600 }}> /{r.emails_sent ?? 0}</span>
          </div>
          <div style={{ fontSize: 12, color: '#3D4A5C', marginBottom: 14 }}>
            <strong style={{ color: r.recovery_rate_pct >= 10 ? '#0D7A3E' : '#0D1B2A' }}>{r.recovery_rate_pct ?? 0}%</strong> recovered · <strong style={{ color: '#0D1B2A' }}>{fmtAED(r.recovered_revenue_aed)}</strong> back
          </div>
          <CcStat label="Open abandonments" value={r.open_abandonments ?? 0} suffix="awaiting email" />
          <CcStat label="Recovery emails sent" value={r.emails_sent ?? 0} suffix="lifetime" />
          <CcStat label="Recovered → paid" value={r.recovered ?? 0} suffix={`= ${fmtAED(r.recovered_revenue_aed)}`} />
        </CcCard>

        {/* CARD 3 · COHORT APPLICATIONS */}
        <CcCard tone="#3A4A5C" label="Cohort Applications" testId="cc2-cohort">
          <div style={{ fontFamily: 'Georgia, serif', fontSize: 38, fontWeight: 700, lineHeight: 1, color: '#0D1B2A', letterSpacing: '-.012em', marginBottom: 4 }}>
            {(c.cohort1_apps ?? 0) + (c.general_apps_total ?? 0)}
          </div>
          <div style={{ fontSize: 12, color: '#3D4A5C', marginBottom: 14 }}>
            <strong style={{ color: '#0D1B2A' }}>{c.cohort1_apps ?? 0}</strong> Cohort 1 · <strong style={{ color: '#0D1B2A' }}>{c.general_apps_total ?? 0}</strong> general
          </div>
          <CcStat label="Today" value={c.cohort1_today ?? 0} suffix="apps" />
          <CcStat label="Last 7 days" value={c.cohort1_last_7d ?? 0} suffix="apps" />
          <CcStat label="Latest applicant" value={(c.recent_apps?.[0]?.full_name) || '—'} suffix={c.recent_apps?.[0]?.created_at ? fmtDate(c.recent_apps[0].created_at) : ''} small />
        </CcCard>

        {/* CARD 4 · VISITORS (Umami) — graceful fallback */}
        <CcCard tone="#0D1B2A" label="Visitors · Last 7d" testId="cc2-visitors">
          {v ? (
            <>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: 38, fontWeight: 700, lineHeight: 1, color: '#0D1B2A', letterSpacing: '-.012em', marginBottom: 4 }}>
                {Number(v.visitors_7d || 0).toLocaleString()}
              </div>
              <div style={{ fontSize: 12, color: '#3D4A5C', marginBottom: 14 }}>
                <strong style={{ color: '#0D1B2A' }}>{Number(v.pageviews_7d || 0).toLocaleString()}</strong> pageviews · <strong>{v.bounce_pct || 0}%</strong> bounce
              </div>
              <CcStat label="Source" value="Umami Cloud" small />
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 130, gap: 8 }}>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: 18, color: '#0D1B2A', fontStyle: 'italic', lineHeight: 1.35 }}>
                Connect Umami API key to see live visitors
              </div>
              <div style={{ fontSize: 11.5, color: '#7A8190', lineHeight: 1.55 }}>
                Snippet is already deployed. To unlock the count, add <code style={{ background: '#FAF7F0', padding: '1px 4px', fontSize: 10.5 }}>UMAMI_API_KEY</code> + <code style={{ background: '#FAF7F0', padding: '1px 4px', fontSize: 10.5 }}>UMAMI_WEBSITE_ID</code> to backend env.
              </div>
              <a href="https://cloud.umami.is/settings/profile" target="_blank" rel="noreferrer" style={{ alignSelf: 'flex-start', marginTop: 4, fontSize: 10.5, fontWeight: 700, letterSpacing: '.16em', color: '#C8A84B', textTransform: 'uppercase', textDecoration: 'none', borderBottom: '1px solid #C8A84B' }}>
                Get API key →
              </a>
            </div>
          )}
        </CcCard>
      </div>
    </div>
  );
}

function CcCard({ tone, label, children, testId, gradient }) {
  return (
    <div data-testid={testId} style={{
      background: gradient ? 'linear-gradient(180deg, #FFFFFF 0%, #FAF7F0 100%)' : '#FFFFFF',
      border: '1px solid #E8DFC9', borderTop: `3px solid ${tone}`,
      padding: '20px 22px', position: 'relative',
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.28em', color: tone, textTransform: 'uppercase', marginBottom: 14 }}>{label}</div>
      {children}
    </div>
  );
}

function CcStat({ label, value, suffix, bar = false, pct = 0, small = false }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: small ? 'flex-start' : 'baseline', padding: '6px 0', borderBottom: '1px dotted rgba(13,27,42,.08)', gap: 10, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 11, color: '#7A8190', letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 600 }}>{label}</span>
      <span style={{ textAlign: 'right' }}>
        <strong style={{ fontSize: small ? 12.5 : 14, color: '#0D1B2A', fontFamily: 'Georgia, serif', fontWeight: 700 }}>{value}</strong>
        {suffix && <span style={{ fontSize: 10.5, color: '#7A8190', marginLeft: 6 }}>{suffix}</span>}
        {bar && (
          <div style={{ width: 80, height: 4, background: '#E8DFC9', marginTop: 4 }}>
            <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: '#C8A84B', transition: 'width .4s ease' }} />
          </div>
        )}
      </span>
    </div>
  );
}
